import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CommonTable from '../../../../components/Shared/Tables/TableV1.tsx';
import { FormDrawer } from '../../../../components/ui';
import { useSnackBar } from '../../../../components/FlowBuilder/Common/SnackBar/index.tsx';
import { workspaceService } from '../../services/workspaceService';

const DEFAULT_DESCRIPTION = 'Handles service requests';

function getErrorMessage(error, fallback) {
  return error?.response?.data?.data?.general_error
    || error?.response?.data?.message
    || error?.message
    || fallback;
}

export default function WorkspaceMappingsTab({ workspace, onClose }) {
  const { showSnackBar } = useSnackBar();
  const showSnackBarRef = useRef(showSnackBar);
  showSnackBarRef.current = showSnackBar;
  const [mappings, setMappings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingMapping, setEditingMapping] = useState(null);
  const [selectedChild, setSelectedChild] = useState(null);
  const [description, setDescription] = useState('');
  const [serviceOptions, setServiceOptions] = useState([]);
  const [optionsPage, setOptionsPage] = useState(1);
  const [hasMoreOptions, setHasMoreOptions] = useState(false);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [mappingToDelete, setMappingToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchMappings = useCallback(async () => {
    if (!workspace?.id) return;
    setIsLoading(true);
    setLoadError('');
    try {
      const response = await workspaceService.getWorkspaceMappings(workspace.id);
      if (response?.status !== 200) {
        throw new Error(response?.data?.message || 'Failed to load business service mappings');
      }
      const childMappings = response.data?.child_mappings;
      setMappings(Array.isArray(childMappings) ? childMappings : []);
    } catch (error) {
      const message = getErrorMessage(error, 'Failed to load business service mappings');
      setLoadError(message);
      showSnackBarRef.current(message, 'error');
    } finally {
      setIsLoading(false);
    }
  }, [workspace?.id]);

  useEffect(() => {
    fetchMappings();
  }, [fetchMappings]);

  const loadServiceOptions = useCallback(async (page = 1, append = false) => {
    setIsLoadingOptions(true);
    try {
      const response = await workspaceService.getBusinessServiceOptions({ next_page: page });
      const result = response?.data ?? response;
      if (!Array.isArray(result?.list)) {
        throw new Error('Unexpected business service list response');
      }
      setServiceOptions((current) => append
        ? [...current, ...result.list]
        : result.list);
      setOptionsPage(page);
      setHasMoreOptions(Boolean(result.has_next) || page < Number(result.pages || 0));
    } catch (error) {
      showSnackBar(
        getErrorMessage(error, 'Failed to load business services'),
        'error'
      );
    } finally {
      setIsLoadingOptions(false);
    }
  }, [showSnackBar]);

  const childOptions = useMemo(() => {
    const filtered = serviceOptions.filter(
      (option) => String(option.proposal_id) !== String(workspace?.id)
    );
    if (
      selectedChild?.proposal_id
      && !filtered.some((option) => String(option.proposal_id) === String(selectedChild.proposal_id))
    ) {
      return [selectedChild, ...filtered];
    }
    return filtered;
  }, [serviceOptions, selectedChild, workspace?.id]);

  const openAddDialog = () => {
    setEditingMapping(null);
    setSelectedChild(null);
    setDescription('');
    setDrawerOpen(true);
    setServiceOptions([]);
    void loadServiceOptions(1);
  };

  const openEditDialog = useCallback((mapping) => {
    setEditingMapping(mapping);
    setSelectedChild({
      proposal_id: mapping.child_campaign_id,
      name: mapping.child_campaign_name,
    });
    setDescription(mapping.description || '');
    setDrawerOpen(true);
    setServiceOptions([]);
    void loadServiceOptions(1);
  }, [loadServiceOptions]);

  const closeDialog = () => {
    if (isSaving) return;
    setDrawerOpen(false);
    setEditingMapping(null);
  };

  const handleSave = async () => {
    if (!selectedChild?.proposal_id) {
      showSnackBar('Select a child business service.', 'warning');
      return;
    }
    if (String(selectedChild.proposal_id) === String(workspace?.id)) {
      showSnackBar('A business service cannot be mapped to itself.', 'warning');
      return;
    }

    const payload = {
      parent_campaign_id: workspace.id,
      parent_campaign_name: workspace.name,
      child_campaign_id: selectedChild.proposal_id,
      child_campaign_name: selectedChild.name,
      description: description.trim() || DEFAULT_DESCRIPTION,
    };

    setIsSaving(true);
    try {
      const response = editingMapping
        ? await workspaceService.updateWorkspaceMapping({
            ...payload,
            _id: editingMapping._id,
          })
        : await workspaceService.createWorkspaceMapping(payload);
      if (response?.status !== 200 && response?.status !== 201) {
        throw new Error(response?.data?.message || 'Failed to save business service mapping');
      }
      showSnackBar(
        `Mapping ${editingMapping ? 'updated' : 'created'} successfully.`,
        'success'
      );
      setDrawerOpen(false);
      setEditingMapping(null);
      await fetchMappings();
    } catch (error) {
      showSnackBar(
        getErrorMessage(error, 'Failed to save business service mapping'),
        'error'
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!mappingToDelete?._id) return;
    setIsDeleting(true);
    try {
      const response = await workspaceService.deleteWorkspaceMapping(mappingToDelete._id);
      if (response?.status !== 200 && response?.status !== 201) {
        throw new Error(response?.data?.message || 'Failed to delete business service mapping');
      }
      setMappings((current) =>
        current.filter((mapping) => mapping._id !== mappingToDelete._id)
      );
      showSnackBar('Mapping deleted successfully.', 'success');
      setMappingToDelete(null);
    } catch (error) {
      showSnackBar(
        getErrorMessage(error, 'Failed to delete business service mapping'),
        'error'
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const columns = useMemo(() => [
    {
      accessorKey: 'parent_campaign_name',
      header: 'Parent business service',
      enableSorting: false,
      Cell: ({ row }) => row.original.parent_campaign_name || workspace?.name || '-',
      size: 260,
    },
    {
      accessorKey: 'child_campaign_name',
      header: 'Child business service',
      enableSorting: false,
      Cell: ({ row }) => row.original.child_campaign_name || '-',
      size: 260,
    },
    {
      accessorKey: 'description',
      header: 'Description',
      enableSorting: false,
      Cell: ({ row }) => row.original.description || '-',
      size: 280,
    },
    {
      accessorKey: 'actions',
      header: 'Action',
      enableSorting: false,
      size: 100,
      Cell: ({ row }) => (
        <Stack direction="row" justifyContent="center">
          <Tooltip title="Edit mapping">
            <IconButton
              aria-label={`Edit mapping to ${row.original.child_campaign_name || 'child service'}`}
              onClick={() => openEditDialog(row.original)}
              size="small"
            >
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete mapping">
            <IconButton
              aria-label={`Delete mapping to ${row.original.child_campaign_name || 'child service'}`}
              onClick={() => setMappingToDelete(row.original)}
              size="small"
              color="error"
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ], [openEditDialog, workspace?.name]);

  return (
    <Box sx={{ mt: 2.5 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={1.5}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Business Service Mapping · {workspace?.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Connect this business service to its child services.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          {onClose ? (
            <Tooltip title="Close mapping list">
              <IconButton aria-label="Close mapping list" onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          ) : null}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openAddDialog}
            disableElevation
          >
            Add Child Service
          </Button>
        </Stack>
      </Stack>

      {loadError ? (
        <Alert
          severity="error"
          action={<Button color="inherit" size="small" onClick={fetchMappings}>Retry</Button>}
          sx={{ mb: 2 }}
        >
          {loadError}
        </Alert>
      ) : null}

      <CommonTable
        columns={columns}
        data={mappings}
        state={{ isLoading }}
        initialState={{ density: 'compact' }}
        enableBottomToolbar={false}
        enableTableFooter={false}
        muiTableContainerProps={{ sx: { maxHeight: 420, width: '100%' } }}
      />

      <FormDrawer
        open={drawerOpen}
        onClose={closeDialog}
        title={editingMapping ? 'Edit child service mapping' : 'Add child service mapping'}
        subtitle={workspace?.name}
        submitLabel={editingMapping ? 'Save changes' : 'Create mapping'}
        onSubmit={handleSave}
        isSubmitting={isSaving}
      >
        <Stack spacing={2}>
          <TextField
            label="Parent business service"
            value={workspace?.name || ''}
            fullWidth
            size="small"
            InputProps={{ readOnly: true }}
          />
          <Autocomplete
            options={childOptions}
            value={selectedChild}
            onChange={(_event, value) => setSelectedChild(value)}
            getOptionLabel={(option) => option?.name || option?.proposal_id || ''}
            isOptionEqualToValue={(option, value) =>
              String(option?.proposal_id) === String(value?.proposal_id)
            }
            loading={isLoadingOptions}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Child business service"
                size="small"
                placeholder="Select a business service"
              />
            )}
          />
          {hasMoreOptions ? (
            <Button
              type="button"
              size="small"
              onClick={() => loadServiceOptions(optionsPage + 1, true)}
              disabled={isLoadingOptions}
            >
              {isLoadingOptions ? 'Loading…' : 'Load more business services'}
            </Button>
          ) : null}
          <TextField
            label="Description"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            fullWidth
            size="small"
            multiline
            minRows={2}
            placeholder="e.g. Handles service requests"
          />
        </Stack>
      </FormDrawer>

      <Dialog
        open={Boolean(mappingToDelete)}
        onClose={() => !isDeleting && setMappingToDelete(null)}
      >
        <DialogTitle>Delete mapping?</DialogTitle>
        <DialogContent>
          <Typography>
            Remove the mapping to {mappingToDelete?.child_campaign_name || 'this child service'}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMappingToDelete(null)} disabled={isDeleting}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={isDeleting}>
            {isDeleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
